import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageHeader } from "@/components/PageHeader";
import { InlineError } from "@/components/QueryState";
import {
  ROLE_HINT,
  ROLE_LABEL,
  describeAccessEvent,
  formatWhen,
  personName,
} from "@/components/boo/access-copy";
import { TakeOwnershipDialog } from "@/components/boo/TakeOwnershipDialog";
import { HandoverSheet } from "@/components/grants/HandoverSheet";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { api, type BooMember, type BooPermissions, type BooRole } from "@/lib/api";
import { useSession } from "@/lib/auth-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Loader2, Plus, UserCog, UserMinus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const ROLES: BooRole[] = ["owner", "admin", "member"];

// Roles the viewer may hand out or take away. Members are an admin's call;
// admins and owners are an owner's.
function assignableRoles(p: BooPermissions | undefined): BooRole[] {
  if (p?.canManageAdmins) return ROLES;
  if (p?.canManageMembers) return ["member"];
  return [];
}

/** "Access" tab: who can open and manage this Boo in the dashboard, and its access history. */
export function AccessTab() {
  const params = useParams({ strict: false }) as { containerId?: string; orgId?: string };
  // Org owners and admins can hand over any Boo, as on the org Access page.
  const orgPerms = useQuery({ queryKey: ["org-permissions", params.orgId], queryFn: () => api.org.permissions() });
  const containerId = params.containerId!;
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const myUserId = session?.user.id;

  const members = useQuery({
    queryKey: ["boo-members", containerId],
    queryFn: () => api.containers.members.list(containerId),
  });
  const events = useQuery({
    queryKey: ["boo-access-events", containerId],
    queryFn: () => api.containers.members.events(containerId),
  });
  const boos = useQuery({ queryKey: ["containers"], queryFn: api.containers.list });
  const boo = boos.data?.data.find((b) => b.id === containerId);
  const booName = boo?.name ?? "this Boo";

  const perms = members.data?._permissions;
  const assignable = assignableRoles(perms);
  const [adding, setAdding] = useState(false);
  const [taking, setTaking] = useState(false);
  const [handingOver, setHandingOver] = useState(false);
  const [removing, setRemoving] = useState<BooMember | null>(null);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["boo-members", containerId] });
    queryClient.invalidateQueries({ queryKey: ["boo-access-events", containerId] });
    queryClient.invalidateQueries({ queryKey: ["containers"] });
  };
  const setRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: BooRole }) =>
      api.containers.members.setRole(containerId, userId, role),
    onSuccess: refresh,
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't change the role"),
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api.containers.members.remove(containerId, userId),
    onSuccess: () => {
      refresh();
      setRemoving(null);
    },
  });

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-auto p-6 gap-8">
      <PageHeader
        title="Access"
        subtitle="Who can open and manage this Boo here in the dashboard. To choose who can chat with it in Slack, Teams, and other apps, go to People."
      />

      {members.data?.ownerless && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300/50 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-200">
          <span>This Boo has no owner. Nobody can add admins or owners until it has one.</span>
          {boo?._permissions?.canTakeOwnership && !perms?.canManageAdmins && (
            <Button size="sm" variant="outline" onClick={() => setTaking(true)}>
              Take ownership
            </Button>
          )}
        </div>
      )}

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold">People with access</h2>
          <div className="flex gap-2">
            {(perms?.canManageAdmins || boo?._permissions?.canTakeOwnership || orgPerms.data?._permissions?.canOverrideAccess) && (
              <Button size="sm" variant="outline" onClick={() => setHandingOver(true)}>
                <UserCog />
                Hand over
              </Button>
            )}
            {assignable.length > 0 && (
              <Button size="sm" onClick={() => setAdding(true)}>
                <Plus />
                Add someone
              </Button>
            )}
          </div>
        </div>
        {members.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-12 w-full rounded-lg" />
            <Skeleton className="h-12 w-full rounded-lg" />
          </div>
        ) : members.isError ? (
          <InlineError error={members.error} />
        ) : members.data!.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nobody has access yet.</p>
        ) : (
          <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {members.data!.data.map((m) => {
              const isMe = m.userId === myUserId;
              const canChange = assignable.includes(m.role) && !isMe;
              const canPickRole = canChange && assignable.length > 1;
              return (
                <div key={m.userId} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium">
                      {personName(m)}
                      {isMe && <span className="font-normal text-muted-foreground"> (you)</span>}
                    </span>
                    {m.name && m.email && <span className="truncate text-xs text-muted-foreground">{m.email}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {canPickRole ? (
                      <Select
                        value={m.role}
                        onValueChange={(role) => setRole.mutate({ userId: m.userId, role: role as BooRole })}
                        disabled={setRole.isPending}
                      >
                        <SelectTrigger id={`boo-role-${m.userId}`} className="h-8 w-28" aria-label={`Role of ${personName(m)}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {assignable.map((r) => (
                            <SelectItem key={r} value={r}>
                              {ROLE_LABEL[r]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="w-28 text-right text-sm text-muted-foreground">{ROLE_LABEL[m.role]}</span>
                    )}
                    {(canChange || isMe) && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-8"
                        title={isMe ? "Leave this Boo" : `Remove ${personName(m)}`}
                        onClick={() => setRemoving(m)}
                      >
                        <UserMinus className="size-4" />
                        <span className="sr-only">{isMe ? "Leave this Boo" : `Remove ${personName(m)}`}</span>
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          {ROLES.map((r) => `${ROLE_LABEL[r]}: ${ROLE_HINT[r]}`).join(" ")}
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-base font-semibold">History</h2>
        {events.isLoading ? (
          <Skeleton className="h-24 w-full rounded-lg" />
        ) : events.isError ? (
          <InlineError error={events.error} />
        ) : events.data!.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">No changes yet.</p>
        ) : (
          <ol className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {events.data!.data.map((e) => (
              <li key={e.id} className="flex flex-col gap-0.5 px-4 py-2.5">
                <span className="text-sm">{describeAccessEvent(e)}</span>
                {e.reason && <span className="text-sm text-muted-foreground">Reason: “{e.reason}”</span>}
                <span className="text-xs text-muted-foreground tabular-nums">{formatWhen(e.createdAt)}</span>
              </li>
            ))}
          </ol>
        )}
      </section>

      {adding && (
        <AddPersonDialog
          containerId={containerId}
          booName={booName}
          roles={assignable}
          onClose={() => setAdding(false)}
          onAdded={refresh}
        />
      )}
      <TakeOwnershipDialog containerId={containerId} booName={booName} open={taking} onOpenChange={setTaking} />
      {handingOver && <HandoverSheet booId={containerId} onClose={() => setHandingOver(false)} />}
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={removing?.userId === myUserId ? `Leave ${booName}?` : `Remove ${personName(removing)}?`}
        description={
          removing?.userId === myUserId
            ? "You'll lose access to this Boo until someone adds you back."
            : `${personName(removing)} will lose access to this Boo in the dashboard.`
        }
        confirmLabel={removing?.userId === myUserId ? "Leave" : "Remove"}
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.userId)}
      >
        {remove.isError && <InlineError error={remove.error} />}
      </ConfirmDialog>
    </div>
  );
}

function AddPersonDialog({
  containerId,
  booName,
  roles,
  onClose,
  onAdded,
}: {
  containerId: string;
  booName: string;
  roles: BooRole[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const candidates = useQuery({
    queryKey: ["boo-member-candidates", containerId],
    queryFn: () => api.containers.members.candidates(containerId),
  });
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<BooRole>("member");
  const add = useMutation({
    mutationFn: () => api.containers.members.add(containerId, userId, role),
    onSuccess: () => {
      onAdded();
      onClose();
    },
  });
  const people = candidates.data?.data ?? [];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add someone to {booName}</DialogTitle>
          <DialogDescription>Anyone in your organization can be added.</DialogDescription>
        </DialogHeader>
        {candidates.isLoading ? (
          <Skeleton className="h-9 w-full" />
        ) : candidates.isError ? (
          <InlineError error={candidates.error} />
        ) : people.length === 0 ? (
          <p className="text-sm text-muted-foreground">Everyone in your organization already has access.</p>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="boo-add-person">Person</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger id="boo-add-person" className="w-full">
                  <SelectValue placeholder="Choose someone" />
                </SelectTrigger>
                <SelectContent>
                  {people.map((p) => (
                    <SelectItem key={p.userId} value={p.userId}>
                      {personName(p)}
                      {p.name && p.email ? ` · ${p.email}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="boo-add-role">Role</Label>
              <Select value={role} onValueChange={(r) => setRole(r as BooRole)}>
                <SelectTrigger id="boo-add-role" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r} value={r}>
                      {ROLE_LABEL[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{ROLE_HINT[role]}</p>
            </div>
          </div>
        )}
        {add.isError && <InlineError error={add.error} />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!userId || add.isPending} onClick={() => add.mutate()}>
            {add.isPending && <Loader2 className="size-3.5 animate-spin" />}
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
